/**
 * @file Rebirth (prestige) : reset de la run contre des Ruby, bonus permanents Damage/Speed/Money x1.05/niveau, Rebirth x3 une fois par jour.
 */
import { Decimal } from '@shared/bignum';
import { UPGRADE_IDS } from '@shared/enums';
import { fail, ok } from '@shared/types';
import type { Result } from '@shared/types';
import { dayKey } from '../Clock';
import type { SimContext } from '../Context';
import { rebirthMultiplier, rebirthRubyGain, rebirthUpgradeCost } from '../balance/formulas';
import type { RebirthStat } from '../state/PlayerData';

export class RebirthService {
  constructor(private readonly ctx: SimContext) {}

  isUnlocked(): boolean {
    return this.ctx.data.progress.bestStageValue >= this.ctx.config.balance.rebirth.unlockStageValue;
  }

  previewRubies(multiplied = false): Decimal {
    const base = rebirthRubyGain(this.ctx.config.balance, this.ctx.data.run.moneyAccum).times(this.ctx.stats.get('rebirthGain'));
    return (multiplied ? base.times(this.ctx.config.balance.rebirth.dailyMultiplier) : base).floor();
  }

  canRebirth(): boolean {
    return this.isUnlocked() && this.previewRubies().gte(1);
  }

  dailyBonusAvailable(): boolean {
    return this.ctx.data.rebirth.dailyBonusDay !== dayKey(this.ctx.rt.wallMs);
  }

  upgradeCost(stat: RebirthStat): Decimal {
    return rebirthUpgradeCost(this.ctx.config.balance, this.ctx.data.rebirth.levels[stat]);
  }

  multiplier(stat: RebirthStat): Decimal {
    return rebirthMultiplier(this.ctx.config.balance, this.ctx.data.rebirth.levels[stat]);
  }

  /**
   * Rebirth : Ruby = formule, run reinitialisee (argent, upgrades, stage). Conserves : armes, etoiles, skins, gemmes, Ruby, record de
   * stage (deblocages), missions, passe, reliques, familiers, buffs.
   */
  perform(useDailyBonus: boolean): Result<{ rubies: Decimal }> {
    const ctx = this.ctx;
    if (!this.canRebirth()) return fail('locked');
    if (useDailyBonus && !this.dailyBonusAvailable()) return fail('cooldown');
    const rubies = this.previewRubies(useDailyBonus);
    const d = ctx.data;
    ctx.wallet.grant({ ruby: rubies }, 'rebirth');
    d.rebirth.count++;
    d.rebirth.totalRubyEarned = d.rebirth.totalRubyEarned.plus(rubies);
    if (useDailyBonus) d.rebirth.dailyBonusDay = dayKey(ctx.rt.wallMs);
    ctx.wallet.reset('money', 'rebirth');
    const start = ctx.config.balance.upgrades.startLevel;
    for (const id of UPGRADE_IDS) d.run.upgrades[id] = start;
    d.run.moneyAccum = new Decimal(0);
    d.run.stageValue = 0;
    d.run.bestStageValue = 0;
    d.run.mainPhase = 'challenge';
    d.run.playTimeSeconds = 0;
    ctx.bus.emit('ModifiersDirty', { source: 'rebirth' });
    ctx.services.missions.increment('rebirths', 1);
    ctx.bus.emit('RebirthDone', { count: d.rebirth.count, rubies, multiplied: useDailyBonus });
    ctx.modes.returnHome('rebirth');
    ctx.bus.emit('SaveRequested', { reason: 'rebirth' });
    return ok({ rubies });
  }

  /** Achat de `count` (ou 'max') niveaux d'un bonus permanent en Ruby (cout croissant par niveau). */
  upgrade(stat: RebirthStat, count: number | 'max'): Result<{ count: number }> {
    const ctx = this.ctx;
    const cfg = ctx.config.balance.rebirth;
    const levels = ctx.data.rebirth.levels;
    const wanted = count === 'max' ? Number.POSITIVE_INFINITY : Math.floor(count);
    if (wanted < 1) return fail('invalidArgument');
    let budget = ctx.wallet.get('ruby');
    let level = levels[stat];
    let total = new Decimal(0);
    let n = 0;
    while (n < wanted && level < cfg.maxLevel) {
      const c = rebirthUpgradeCost(ctx.config.balance, level);
      if (budget.lt(c)) break;
      budget = budget.sub(c);
      total = total.plus(c);
      level++;
      n++;
    }
    if (n === 0) return fail('notEnoughFunds');
    ctx.wallet.spend({ ruby: total }, `rebirthUpgrade:${stat}`);
    levels[stat] = level;
    ctx.bus.emit('ModifiersDirty', { source: `rebirth:${stat}` });
    ctx.bus.emit('RebirthUpgraded', { stat, level });
    return ok({ count: n });
  }
}
